import React, { useEffect, useState } from "react";
import type { Format, FormatStyle } from "@github/relative-time-element";

import { IS_NODE_BROWSERLESS, IS_TESTING } from "@/__main__/constants.mjs";
import { formatDuration } from "@/app/util.mjs";
import { devWarn } from "@/util/dev.mjs";
import { getLocale } from "@/util/i18n-helper.mjs";

type TimeAgoProps = {
  date?: Date | number;
  className?: string;
  format?: Format;
  formatStyle?: FormatStyle;
};

type TimeUntilProps = {
  date?: Date | number;
  className?: string;
  formatStyle?: FormatStyle;
};

type TimerProps = {
  startTime: Date | number;
  format?: string;
};

if (!IS_NODE_BROWSERLESS) {
  import("@github/relative-time-element");
}

function castDate(date) {
  if (!(date instanceof Date)) {
    date = new Date(date);
  }
  if (date instanceof Date && !Number.isNaN(date.getTime())) {
    date = date.toISOString();
  } else {
    devWarn("an invalid Date object was passed into Time component", date);
    date = `${date}`;
  }
  return date;
}

// This is meant to be used to display a proper duration, but only
// *if it is uncertain whether the date is past or future*.
export function TimeRelative(props) {
  const isPast = new Date(props.date).getTime() < Date.now();
  return isPast ? TimeAgo(props) : TimeUntil(props);
}

export function TimeAgo({
  date,
  className,
  format = "relative",
  formatStyle,
}: TimeAgoProps) {
  // prevent dangling timers
  if (IS_TESTING) {
    const dateText = `${date}`;
    return <span>{dateText}</span>;
  }
  return (
    <relative-time
      tense="past"
      // @ts-ignore
      class={className}
      datetime={castDate(date)}
      lang={getLocale()}
      format={format}
      formatStyle={formatStyle}
      suppressHydrationWarning
      minute="2-digit"
    />
  );
}

export function TimeUntil({ date, className, formatStyle }: TimeUntilProps) {
  // prevent dangling timers
  if (IS_TESTING) {
    const dateText = `${date}`;
    return <span>{dateText}</span>;
  }
  return (
    <relative-time
      tense="future"
      // @ts-ignore
      class={className}
      datetime={castDate(date)}
      lang={getLocale()}
      format={"relative"}
      formatStyle={formatStyle}
      suppressHydrationWarning
    />
  );
}

export const Timer = ({ startTime, format }: TimerProps) => {
  const [timepassed, setTimePassed] = useState(
    startTime &&
      formatDuration(
        new Date().getTime() - new Date(startTime).getTime(),
        format || "mm:ss",
      ),
  );

  useEffect(() => {
    if (!startTime) return;
    let timer;
    const setTime = (time) => {
      timer = setTimeout(() => {
        setTimePassed(
          formatDuration(new Date().getTime() - time, format || "mm:ss"),
        );
        setTime(time);
      }, 1000);
    };
    setTime(startTime);
    return () => clearTimeout(timer);
  }, [format, startTime]);

  return timepassed;
};
